import React from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TSecretSelectProps } from '@/console/features/vaults/components/secret-select/secret-select.types';
import { TSelectOption } from '@/shared/components/ui/form/components/searchable-select/base-searchable-select.types';
import { AsyncSearchableSelect } from '@/shared/components/ui/form/components/searchable-select/variant/async/async-searchable-select';
import { useReadSecretsForVaultQuery } from '@/shared/redux/api/assets/vaults/vaults-api';
import {
  ReadSecretsForVaultQuery,
  SecretFragment,
} from '@/shared/types/types.generated';

export const SecretSelect = <
  TFieldValues extends FieldValues,
  TOptionShape = string,
>({
  name,
  createOption,
  disableTags = true,
  additionalOptions = [],
  onSelectChange,
  getItemId,
  required,
  vaultId,
  secretFilter,
}: TSecretSelectProps<TFieldValues, TOptionShape>) => {
  const { t } = useTranslation();
  const createDefaultOption = (secret: SecretFragment) => {
    return {
      label: secret.name,
      value: secret.secretId,
      additionalInfo: t(
        `vault:secrets.type.${secret.secretType.toLowerCase()}`,
      ),
    } as TSelectOption<TOptionShape>;
  };
  const _createOption = createOption ? createOption : createDefaultOption;

  const transformData = (data?: ReadSecretsForVaultQuery) => {
    if (!data) {
      return [];
    }

    const secrets = data.readSecretsForVault.items;
    const validSecrets = secretFilter
      ? secrets.filter((vault) => secretFilter(vault))
      : secrets;

    const options = validSecrets.map((secret) => _createOption(secret));

    return [...additionalOptions, ...options];
  };

  return (
    <AsyncSearchableSelect<TFieldValues, ReadSecretsForVaultQuery, TOptionShape>
      name={name}
      label={t('vault:secrets.select_secret')}
      placeholder={t('vault:secrets.search_secret')}
      disableTags={disableTags}
      getItemId={getItemId}
      optionProps={{
        useRTKQuery: useReadSecretsForVaultQuery,
        rtkQueryArgs: { vaultId },
        transformData,
      }}
      onSelectChange={onSelectChange}
      required={required}
    />
  );
};
